/** @jsxImportSource @emotion/react */
import { SortDirection } from '../general/models/sort-direction'
import {
    useChangeOrderExemptionStatusMutation,
    useLazyGetCompanyOrdersQuery,
    useRegenerateOrderMutation,
    useRegenerateOrderAndCallbackMutation,
} from './orders.service'
import React, { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useAppSelector } from '../../redux/hooks'
import { selectOrdersFilters } from './order.slice'
import { usePrevious } from '../general/use-previous'
import { Paper, Tooltip, Typography } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { KotiTable } from '../general/table/koti-table'
import { OrderTableItem } from './models/order'
import { Loader } from '../general/loader/loader'
import { Config } from '../../redux/config'
import { TFunction } from 'i18next'
import { TableStructure } from '../general/table/table-structure.type'
import { formatDateTime } from '../../utils/date-utils'
import { getProductList } from './orders-helper'
import { useGetCompanyDepartmentsUsersQuery } from '../../redux/apis/companies-api'
import { User } from '../users/user'
import { MultiStringViewer } from '../general/multi-viewer/multi-string-viewer'
import TablePagination from '@mui/material/TablePagination'
import { OrdersTableFilters } from './OrdersTableFilters'
import { TableActionMenuProps } from 'types/util'
import { DataGridActionsCell } from 'components/general/table/dataGrid/DataGridActionsCell'
import { AdminOrdersActionsMenu } from './components/admin-orders-actions-menu'
import ConfirmDialogV2 from 'components/general/dialogs/confirm-dialog-v2'
import { EXEMPT_ORDER_INVOICE_ID } from 'constants/orders'
import { useAuthContextRole } from '../auth/auth.slice'
import { Role } from '../auth/role'

const onDownload = (object: OrderTableItem) => {
    const link = document.createElement('a')
    link.href = `${Config.backendUrl}/v1/orders/${object.orderId}/pdf/${object.index}?accessToken=${object.pdf.accessToken}`
    link.click()
}

const getOrdersTableStructure = (
    t: TFunction,
    openActionMenu: (event: React.MouseEvent<HTMLElement>, rowId: string) => void,
    isCubitAdmin: boolean,
    users?: User[],
): TableStructure<OrderTableItem>[] => [
    {
        label: t('TEXT_DATE', 'Dato'),
        columnName: 'created',
        sortFunction: (object) => object.created.getTime(),
        value: (object) => formatDateTime(object.created),
    },
    {
        label: t('TEXT_PROJECT_NR', 'Oppdragsnr.'),
        value: (object) => object.note,
    },
    {
        label: t('TEXT_ORDER_NR', 'Ordrenr.'),
        columnName: 'orderId',
        sortFunction: (object) => object.orderNumber,
        value: (object) => object.orderNumber,
    },
    {
        label: t('TEXT_ORDER_INVOICE_ID', 'Faktura-ID'),
        columnName: 'invoiceId',
        value: (object) =>
            object.invoiceId === EXEMPT_ORDER_INVOICE_ID ? (
                <Typography variant="body2" sx={(theme) => ({ color: theme.palette.error.main })}>
                    {t('TEXT_ORDER_EXEMPTED', 'Fritatt')}
                </Typography>
            ) : (
                <Typography
                    variant="body2"
                    sx={(theme) => ({
                        color: object.invoiceId ? theme.palette.success.main : theme.palette.text.primary,
                    })}
                >
                    {object.invoiceId || t('TEXT_ORDER_NOT_INVOICED_YET', 'Not invoiced')}
                </Typography>
            ),
    },
    {
        label: t('TEXT_ORDER_BY', 'Bestilt av'),
        columnName: 'userId',
        sortFunction: (object) => object.userId,
        value: (object) => {
            if (users) {
                const user = users.find((u) => u.id === object.userId)
                return user?.name
            }
            return '-'
        },
    },
    {
        label: t('TEXT_ADDRESS', 'Adresse'),
        sortFunction: (object) => (object.address ? object.address.addressText : ''),
        value: (object) =>
            object.address ? <MultiStringViewer items={object.address.addressText.split(',')} owners={false} /> : '',
    },
    {
        label: t('TEXT_POStAL_CODE', 'Postnummer'),
        sortFunction: (object) => object.address.postalCode,
        value: (object) => object.address && `${object.address.postalCode || ''} ${object.address.postalName || ''}`,
    },
    {
        label: t('TEXT_CADASTRE_NR', 'Matrikkelnr.'),
        sortFunction: (object) => object.cadastreNr,
        value: (object) => object.cadastreNr,
    },
    {
        label: t('TEXT_STATUS'),
        sortFunction: (object) => object.status,
        value: (object) => (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {t(`ORDER_ITEM_STATUS_${object.status}`)}
                {isCubitAdmin && object.failures && object.failures.length > 0 && (
                    <Tooltip
                        arrow
                        title={
                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                                    {t('TEXT_ORDER_GENERATION_PROBLEMS', 'Generation problems')}
                                </div>
                                {object.failures.map((failure, index) => (
                                    <div key={index}>
                                        {formatDateTime(failure.occurredAt)} — {failure.reason}
                                    </div>
                                ))}
                            </div>
                        }
                    >
                        <ErrorOutlineIcon color="error" fontSize="small" sx={{ cursor: 'help' }} />
                    </Tooltip>
                )}
            </span>
        ),
    },
    {
        label: '',
        value: (row) => <DataGridActionsCell rowId={row.uniqueId} onOpen={openActionMenu} />,
    },
]

type CompanyProps = {
    companyId: string
    reload: any
}

export const MyCompanyOrders: React.FC<CompanyProps> = ({ companyId, reload }) => {
    const { t } = useTranslation()
    const isCubitAdmin = useAuthContextRole() === Role.CubitAdmin
    const filters = useAppSelector(selectOrdersFilters)
    const prevFilters = usePrevious(filters)

    const [pageNumber, setPageNumber] = useState(0)
    const [itemsPerPage, setItemsPerPage] = useState(10)
    const [sortBy, setSortBy] = useState('created')
    const [direction, setDirection] = useState<SortDirection>('desc')
    const [anchorElProps, setAnchorPropsEl] = React.useState<TableActionMenuProps>(null)
    const [selectedOrderIdToExempt, setSelectedOrderIdToExempt] = React.useState<string | null>(null)

    const [regenerate] = useRegenerateOrderMutation()
    const [regenerateAndCallback] = useRegenerateOrderAndCallbackMutation()
    const [changeExemptionStatus, { isLoading: isLoadingExemptChange }] = useChangeOrderExemptionStatusMutation()
    const [trigger, { data, isSuccess }] = useLazyGetCompanyOrdersQuery()
    const { data: users } = useGetCompanyDepartmentsUsersQuery({
        page: 0,
        itemsPerPage: 9999,
        sortBy: 'id',
        direction: 'asc',
        companyId: companyId ?? '',
    })

    const params = {
        pagination: { page: pageNumber, itemsPerPage: itemsPerPage, sortBy, direction, ...filters },
        companyId,
    }

    const withoutCacheSubmit = () => trigger(params, false)

    useEffect(() => {
        withoutCacheSubmit()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reload])

    useEffect(() => {
        if (prevFilters === filters) {
            trigger(params)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageNumber, itemsPerPage, direction, trigger, sortBy, filters, prevFilters])

    const handleOnSortChange = (orderBy: string, order: SortDirection) => {
        setSortBy(orderBy)
        setDirection(order)
    }

    const regen = (orderId: string) => {
        regenerate({ id: orderId })
    }

    const regenAndCallback = (orderId: string) => {
        regenerateAndCallback({ id: orderId })
    }

    const openActionMenu = React.useCallback((event: React.MouseEvent<HTMLElement>, rowId: string) => {
        console.log({ event, rowId })
        setAnchorPropsEl((prev) => (prev ? null : { anchorEl: event.currentTarget, rowId }))
    }, [])

    const closeActionMenu = () => setAnchorPropsEl(null)

    const handleChangeOrderExemptionState = async () => {
        if (!selectedOrderToChangeExemptState) throw new Error('Cant call order exemption with undefined id')

        await changeExemptionStatus({
            id: selectedOrderToChangeExemptState.id,
            exempted: selectedOrderToChangeExemptState.invoiceId !== EXEMPT_ORDER_INVOICE_ID,
        }).unwrap()
        setSelectedOrderIdToExempt(null)
    }

    const handleDownloadOrder = (id: string) => {
        closeActionMenu()
        const product = products.find((p) => p.orderId == id)

        if (!product) throw new Error('Invalid case cant download order without assigned id')

        onDownload(product)
    }

    const onSubmit = () => trigger(params)

    if (!data || !data.data) return <Loader />

    // Server returns whole orders that contain a matching line; narrow the flattened rows to the
    // selected status so siblings of a matched order don't show up under the status filter.
    const products = getProductList(data.data).filter(
        (product) => !filters.itemStatus || product.status === filters.itemStatus,
    )
    const selectedOrderToChangeExemptState = selectedOrderIdToExempt
        ? data?.data.find((o) => o.id === selectedOrderIdToExempt)
        : undefined
    const isCurrentlyExempt = Boolean(selectedOrderToChangeExemptState?.invoiceId === EXEMPT_ORDER_INVOICE_ID)

    return (
        <>
            <Paper elevation={0}>
                <OrdersTableFilters onSubmit={onSubmit} />
                <KotiTable
                    selectable={false}
                    pagination={false}
                    rowsPerPageOptions={[]}
                    tableStructure={getOrdersTableStructure(t, openActionMenu, isCubitAdmin, users?.data)}
                    rowsPerPage={products.length}
                    page={pageNumber}
                    rows={products}
                    total={products.length}
                    onSortChanged={handleOnSortChange}
                    handleChangePage={setPageNumber}
                    handleChangeRowsPerPage={setItemsPerPage}
                />
                <TablePagination
                    labelRowsPerPage={<span>{t('ROWS_PER_PAGE', 'Rader per side')}</span>}
                    component="div"
                    count={data.total}
                    rowsPerPage={itemsPerPage}
                    page={pageNumber}
                    onPageChange={(event, newPage) => setPageNumber(newPage)}
                    onRowsPerPageChange={(event) => setItemsPerPage(parseInt(event.target.value))}
                />
            </Paper>

            <AdminOrdersActionsMenu
                anchorEl={anchorElProps?.anchorEl}
                isCubitAdmin={isCubitAdmin}
                onClose={closeActionMenu}
                onExemptAction={(id) => {
                    closeActionMenu()
                    setSelectedOrderIdToExempt(id)
                }}
                onRegenerateOrderAction={(id) => {
                    closeActionMenu()
                    regen(id)
                }}
                onRegenerateAndCallbackAction={(id) => {
                    closeActionMenu()
                    regenAndCallback(id)
                }}
                onDownloadAction={handleDownloadOrder}
                selectedRow={
                    anchorElProps?.rowId ? products.find((p) => p.uniqueId === anchorElProps.rowId) : undefined
                }
                onRefreshAction={() => {
                    closeActionMenu()
                    withoutCacheSubmit()
                }}
            />

            <ConfirmDialogV2
                handleClose={() => setSelectedOrderIdToExempt(null)}
                open={Boolean(selectedOrderIdToExempt) && isSuccess}
                onConfirm={handleChangeOrderExemptionState}
                content={
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        <Trans
                            i18nKey={isCurrentlyExempt ? 'CONFIRM_UNDO_ORDER_EXEMPT' : 'CONFIRM_EXEMPT_ORDER'}
                            values={{
                                orderNumber: selectedOrderToChangeExemptState?.orderId || '',
                            }}
                            defaults={
                                isCurrentlyExempt
                                    ? 'Do you want to undo exempt for <strong>{{orderNumber}}</strong> order?'
                                    : 'Do you want to exempt <strong>{{orderNumber}}</strong> order?'
                            }
                        />
                    </Typography>
                }
                isLoading={isLoadingExemptChange}
            />
        </>
    )
}
